/**
 * Europe/Rome time helpers (docs/08 §3). Every conversion between UTC instants and domain
 * dates/times goes through here; the process time zone is never used.
 */
import { APP_TIME_ZONE } from './constants.js';
import type { HHMM, IsoDate, IsoDateTime } from './types.js';

/** An instant seen in Europe/Rome. */
export interface RomeDateTime {
  /** The instant, ISO 8601 UTC with milliseconds. */
  iso: IsoDateTime;
  /** Rome calendar date `YYYY-MM-DD`. */
  date: IsoDate;
  /** Rome time `HH:MM`. */
  time: HHMM;
  /** Minutes since Rome midnight (0..1439). */
  minutes: number;
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  weekday: number;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const WEEKDAYS: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

const romeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: APP_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  weekday: 'short',
  hourCycle: 'h23',
});

interface RomeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}

function romeParts(ms: number): RomeParts {
  const parts: Record<string, string> = {};
  for (const p of romeFormatter.formatToParts(new Date(ms))) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // some engines print midnight as "24" even with h23
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: WEEKDAYS[parts.weekday ?? ''] ?? 0,
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function parseInstant(iso: IsoDateTime | Date): number {
  const ms = iso instanceof Date ? iso.getTime() : Date.parse(iso);
  if (Number.isNaN(ms)) throw new RangeError(`Istante non valido: ${String(iso)}`);
  return ms;
}

/** Offset of Rome from UTC, in minutes, at the given instant (+60 in winter, +120 in summer). */
export function romeOffsetMinutes(iso: IsoDateTime | Date): number {
  const ms = parseInstant(iso);
  const p = romeParts(ms);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
}

/** Converts a UTC instant to Rome date, time, minutes since midnight and ISO weekday. */
export function toRome(iso: IsoDateTime | Date): RomeDateTime {
  const ms = parseInstant(iso);
  const p = romeParts(ms);
  return {
    iso: new Date(ms).toISOString(),
    date: `${p.year}-${pad2(p.month)}-${pad2(p.day)}`,
    time: `${pad2(p.hour)}:${pad2(p.minute)}`,
    minutes: p.hour * 60 + p.minute,
    weekday: p.weekday,
  };
}

/** Rome calendar date of a UTC instant. */
export function romeDate(iso: IsoDateTime | Date): IsoDate {
  return toRome(iso).date;
}

/**
 * Converts a Rome local date and time to a UTC instant.
 * Non-existent local times (spring-forward gap) are shifted forward; ambiguous ones
 * (fall-back hour) resolve to the second occurrence (standard time).
 */
export function fromRome(date: IsoDate, time: HHMM = '00:00'): IsoDateTime {
  const d = ISO_DATE_RE.exec(date);
  const t = HHMM_RE.exec(time);
  if (!d) throw new RangeError(`Data non valida: ${date}`);
  if (!t) throw new RangeError(`Ora non valida: ${time}`);
  const naive = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  let ms = naive - romeOffsetMinutes(new Date(naive)) * 60000;
  ms = naive - romeOffsetMinutes(new Date(ms)) * 60000;
  return new Date(ms).toISOString();
}

/** Adds `n` calendar days to a `YYYY-MM-DD` date (pure date arithmetic, no time zone). */
export function addDays(date: IsoDate, n: number): IsoDate {
  const d = ISO_DATE_RE.exec(date);
  if (!d) throw new RangeError(`Data non valida: ${date}`);
  const ms = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]) + n);
  return new Date(ms).toISOString().slice(0, 10);
}

/** Compares two ISO instants: negative if a < b, 0 if equal, positive if a > b. */
export function compareIso(a: IsoDateTime, b: IsoDateTime): number {
  return parseInstant(a) - parseInstant(b);
}

/** Whole minutes from `from` to `to` (can be negative), rounded like the workbook `ROUND(Δ×1440)`. */
export function minutesBetween(from: IsoDateTime, to: IsoDateTime): number {
  return Math.round((parseInstant(to) - parseInstant(from)) / 60000);
}

/** The current instant, ISO UTC with milliseconds. */
export function nowIso(): IsoDateTime {
  return new Date().toISOString();
}

/** The current instant seen in Rome. */
export function romeNow(now: Date = new Date()): RomeDateTime {
  return toRome(now);
}

export function isIsoDate(value: string): boolean {
  const d = ISO_DATE_RE.exec(value);
  if (!d) return false;
  return addDays(value, 0) === value;
}

export function isHHMM(value: string): boolean {
  return HHMM_RE.test(value);
}

// ---------------------------------------------------------------- formatting (it-IT)

/** `YYYY-MM-DD` → `gg/mm/aaaa`. */
export function formatDate(date: IsoDate): string {
  const d = ISO_DATE_RE.exec(date);
  if (!d) return date;
  return `${d[3]}/${d[2]}/${d[1]}`;
}

/** `YYYY-MM-DD` → `gg/mm`. */
export function formatDayMonth(date: IsoDate): string {
  const d = ISO_DATE_RE.exec(date);
  if (!d) return date;
  return `${d[3]}/${d[2]}`;
}

/** UTC instant → Rome `HH:MM`. */
export function formatTime(iso: IsoDateTime): string {
  return toRome(iso).time;
}

/** UTC instant → Rome `gg/mm/aaaa HH:MM`. */
export function formatDateTime(iso: IsoDateTime): string {
  const r = toRome(iso);
  return `${formatDate(r.date)} ${r.time}`;
}

const WEEKDAY_NAMES_IT = [
  'lunedì',
  'martedì',
  'mercoledì',
  'giovedì',
  'venerdì',
  'sabato',
  'domenica',
];

/** Italian weekday name of a `YYYY-MM-DD` date. */
export function weekdayNameIt(date: IsoDate): string {
  const d = ISO_DATE_RE.exec(date);
  if (!d) return '';
  const jsDay = new Date(Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]))).getUTCDay();
  return WEEKDAY_NAMES_IT[(jsDay + 6) % 7] ?? '';
}
